<!--
  a11y: initial focus on the back/close chevron when the route-tracking
  slide-up opens, so SR users land on a labelled button instead of
  document.body / whatever was focused before. Tab lock is provided by the
  shared OverlayComponent focus trap.
-->
<design-slide-up-card
  #card
  [isCardOpen]="!!isCardOpen"
  class="source-destination-route-tracking"
  (onHeaderButtonClick)="closeCard()"
  [showLogoInHeader]="true"
  [title]="trackingRouteText"
  [roleDialog]="true"
  [focusInitialOnHeaderBack]="true"
  [hideHeaderInDesktop]="true"
  [icon]="icon"
  >
  <app-source-destination-results-map
    class="flex-1"
    [vehiclePollRoute]="trackedRoute"
    [vehiclePollTransitSectionIndex]="vehiclePollTransitSectionIndex"
    [showBoardingApproachSegment]="!isUserOnBus"
    >
    <div
      class="flex justify-center items-center gap-2 bg-darkBlue absolute left-0 right-0 top-0 z-50 py-3 px-2 w-full"
      >
      <ng-icon name="info" class="text-white text-xl" />
      <span class="text-white">{{ trackingRouteAlert }}</span>
    </div>

    @if (routePlannerDebugUi) {
      <div class="boarding-debug-overlay absolute z-50">
        <button
          class="boarding-debug-toggle"
          (click)="debugOverlayExpanded = !debugOverlayExpanded"
          type="button"
          >
          Debug {{ debugOverlayExpanded ? 'hide' : 'show' }}
        </button>
        @if (debugOverlayExpanded && boardingDebugState) {
          <div class="boarding-debug-content">
            <div>tick: {{ boardingDebugState.debugTick }} | ~{{ boardingDebugState.checkIntervalMs }}ms</div>
            <div>updated: {{ formatDebugTimestamp(boardingDebugState.updatedAt) }}</div>
            <div>
              sec: state={{ boardingDebugState.trackingSectionIndex }} det={{ boardingDebugState.detectedSectionIndex ?? '-' }}
              brd={{ boardingDebugState.boardingCheckSectionIndex ?? '-' }} ({{ boardingDebugState.currentSectionType || '-' }})
            </div>
            <div>route: {{ boardingDebugState.routeId || '-' }} | nSec={{ boardingDebugState.routeSectionsCount ?? '-' }}</div>
            <div>line: {{ boardingDebugState.transportLineLabel || '-' }}</div>
            <div>onBus: {{ boardingDebugState.isUserOnBus }} | boardedIdx: {{ boardingDebugState.boardedBusSectionIndex }}</div>
            <div>ped→arr(m): {{ formatDebugNumber(boardingDebugState.pedestrianToArrivalMeters) }}</div>
            <div>
              nextStop(m): {{ formatDebugNumber(boardingDebugState.distanceToNextTransitBoardingStopMeters) }}
              | nearPrecheck: {{ boardingDebugState.withinNextBoardingStopPrecheck }}
            </div>
            <div>
              thr(m): bus{{ boardingDebugState.thrBusBoardingM }} ped{{ boardingDebugState.thrPedCompleteM }} near{{ boardingDebugState.thrNearStopPrecheckM }}
            </div>
            <div>gpsAcc(m): {{ formatDebugNumber(boardingDebugState.gpsAccuracyMeters) }}</div>
            <div>trigger: {{ boardingDebugState.triggerSource }} | skip: {{ boardingDebugState.skipReason || '-' }}</div>
            <div>routeDesc: {{ boardingDebugState.routeDesc || '-' }}</div>
            <div>stopCode: {{ boardingDebugState.stopCode ?? '-' }}</div>
            <div>schedDep (UTC): {{ boardingDebugState.scheduledDepartureIso || '-' }}</div>
            <div>schedDep (local): {{ boardingDebugState.scheduledDepartureLocal || '-' }}</div>
            <div>device now (local): {{ boardingDebugState.deviceNowLocal || '-' }} | TZ: {{ boardingDebugState.deviceTimeZone || '-' }}</div>
            <div>departed: {{ boardingDebugState.busDeparted }} | minSinceDep: {{ formatDebugNumber(boardingDebugState.minutesSinceDeparture) }}</div>
            <div>popup: emitted={{ boardingDebugState.popupEmitted }} shown={{ boardingDebugState.hasShownPopup }} | notifSet={{ boardingDebugState.shownNotificationsCount }}</div>
            <div>dBus(m): {{ formatDebugNumber(boardingDebugState.distanceToBusMeters) }} close={{ boardingDebugState.closeToBus }}</div>
            <div>dStop(m): {{ formatDebugNumber(boardingDebugState.distanceToStopMeters) }} close={{ boardingDebugState.closeToStop }}</div>
            <div>user: {{ formatDebugNumber(boardingDebugState.userLocation?.lat) }}, {{ formatDebugNumber(boardingDebugState.userLocation?.lng) }}</div>
            <div>bus: {{ formatDebugNumber(boardingDebugState.busLocation?.lat) }}, {{ formatDebugNumber(boardingDebugState.busLocation?.lng) }}</div>
            <div>stop: {{ formatDebugNumber(boardingDebugState.stopLocation?.lat) }}, {{ formatDebugNumber(boardingDebugState.stopLocation?.lng) }}</div>
            <div class="boarding-debug-api-msg">api: {{ boardingDebugState.lastScheduleApiMessage || '-' }}</div>
          </div>
        }
      </div>
    }

    @if (!!currentTrackingInstructions?.length) {
      <div
        class="instructions-container absolute left-0 right-0 z-50 bottom-0 flex flex-col gap-2 p-4"
      [ngClass]="{
        'slide-down': !displayInstructions,
        'slide-up': !!displayInstructions
      }"
        (touchstart)="swipeHandler.swipe($event, 'start')"
        (touchend)="changeTrackingStage(swipeHandler.swipe($event, 'end'))"
        >
        @for (
          instruction of currentTrackingInstructions.slice(0, 2); track
          instruction; let i = $index) {
          @if (!isPedestrianActionInstruction(instruction)) {
            <app-source-destination-route-tracking-section
              [isActive]="i == 0"
              [section]="getSection(instruction)"
              [sectionRouteIndex]="getInstructionSectionRouteIndex(instruction)"
              [isUserOnBus]="isUserOnBus"
              [boardedBusSectionIndex]="boardedBusSectionIndex"
              [currentSectionIndex]="currentSection"
              />
          }
          @if (isPedestrianActionInstruction(instruction)) {
            <app-source-destination-route-tracking-action
              [isActive]="i == 0"
              [action]="getAction(instruction)"
              />
          }
        }
      </div>
    }
  </app-source-destination-results-map>
</design-slide-up-card>
